import { useTranslation } from "react-i18next";
import { Alert, Button } from "@h2j-data-team/ui";
import type { QvdAnalysisCoverage } from "@repo/shared";

// staleRunErrorPrefix is how the API's stale-run reaper opens the message it
// writes to run.error (see reapStaleRuns). It is the one failure the PRODUCT
// generates rather than relays, and verbatim it reads as jargon — "reaped by
// scheduler after no heartbeat" says nothing to the person whose analysis just
// stopped — so it is the one message translated instead of shown raw. Matching
// on the prefix, not the whole string, keeps a reworded tail from silently
// falling back to the untranslated text.
const staleRunErrorPrefix = "Stale run:";

export function QvdCoverageBanner({
  coverage,
  error = null,
  onResume,
  resuming = false,
  canResume = false,
}: {
  coverage: QvdAnalysisCoverage | null;
  // The run's failure message, shown as the CAUSE of the gaps below. Without it
  // an interrupted run only ever said what was missing ("not processed: 126"),
  // never why — and the why is the part the user cannot derive.
  error?: string | null;
  // Continue a stopped run's leftover work. When provided and the run is
  // resumable (terminal) with unreached QVDs (not_processed) or rows still
  // awaiting sizing (sizePendingCount), a "Continue" action shows.
  onResume?: () => void;
  resuming?: boolean;
  canResume?: boolean;
}) {
  const { t } = useTranslation("qvd-analysis");
  const failure = error?.trim() ?? "";
  // A run that died before producing coverage at all (bad credentials, no Qlik
  // URL) is the MOST opaque case, so the cause alone is enough to render.
  if (!coverage && !failure) return null;

  // QVDs a stopped run (cancelled / aborted) never attempted — counted into the
  // headline so a partial run's numbers add up instead of hiding the gap.
  const notProcessed = coverage?.reasons?.not_processed ?? 0;
  const notAnalyzableTotal = coverage
    ? coverage.qvdsNotAnalyzable +
      coverage.columnsNotAnalyzable +
      coverage.spacesNotInventoried +
      notProcessed
    : 0;
  // Analyzed QVDs whose columns were found unloaded but the (interrupted) size
  // phase never measured — the run reads 100% coverage yet its wasted bytes are
  // incomplete. Surfaced here so the run does not look done, and a resume can
  // finish the sizing.
  const sizePending = coverage?.sizePendingCount ?? 0;
  if (notAnalyzableTotal <= 0 && sizePending <= 0 && !failure) return null;

  const cause = failure.startsWith(staleRunErrorPrefix) ? t("coverage.causes.staleRun") : failure;

  const reasons = Object.entries(coverage?.reasons ?? {}).filter(([, n]) => n > 0);
  // Resume drives both leftover work: unreached QVDs (not_processed) and rows
  // still awaiting sizing. Either alone is enough to offer "Continue".
  const resumeCount = notProcessed + sizePending;
  const showResume = canResume && !!onResume && resumeCount > 0;

  return (
    <Alert
      tone="warning"
      action={
        showResume && (
          <Button size="sm" variant="outline" loading={resuming} onClick={onResume}>
            {t("coverage.resume", { count: resumeCount })}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-1">
        {notAnalyzableTotal > 0 && coverage && (
          <p>
            {t("coverage.headline", {
              notAnalyzable: coverage.qvdsNotAnalyzable + notProcessed,
              inventoried: coverage.qvdsInventoried,
            })}
          </p>
        )}
        {sizePending > 0 && <p>{t("coverage.sizePendingHeadline", { count: sizePending })}</p>}
        {/* Above the per-reason counts on purpose: those say WHAT is missing,
            this says WHY the run stopped, which is what explains them. */}
        {cause && <p>{t("coverage.cause", { cause })}</p>}
        {reasons.length > 0 && (
          <p className="font-mono text-xs opacity-80">
            {reasons
              .map(([reason, n]) => `${t(`coverage.reasons.${reason}`, reason)}: ${n}`)
              .join(" · ")}
          </p>
        )}
      </div>
    </Alert>
  );
}
